import { Injectable, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  ChildrenOutletContexts,
  NavigationEnd,
  PRIMARY_OUTLET,
  Router,
  RouterOutletContract,
} from '@angular/router'

import { filter } from 'rxjs'

import { isUuid } from '@flowx/designer/modules/ai-builder/utils/text.util'
import {
  RESOURCE_PAGE_DATA_KEY,
  ResourcePageMarker,
  resourcePageId,
} from '@flowx/designer/shared/entities/resource-page.entity'
import type { ComponentCanDeactivate } from '@flowx/designer/shared/guards/pending-changes.guard'

/** The resource types the harness knows: a page of any other type is not reported. */
export const HARNESS_RESOURCE_TYPES = [
  'workflow',
  'ui-flow',
  'system',
  'project-data-type',
  'enumeration',
  'mediafile',
  'substitutiontag',
  'document-template',
  'notification-template',
  'custom-component',
  'process',
] as const
export type HarnessResourceType = (typeof HARNESS_RESOURCE_TYPES)[number]
export type HarnessUiContext = {
  schema_version: 1
  app_version_id: string
  view: 'resource_list' | 'resource_editor'
  resource_type: HarnessResourceType
  resource_id?: string
  has_unsaved_changes?: boolean
}

const isHarnessResourceType = (type: string): type is HarnessResourceType =>
  (HARNESS_RESOURCE_TYPES as readonly string[]).includes(type)

/** Read only explicit semantic route metadata. No URLs, names, query contents or form data leave Designer. */
@Injectable({ providedIn: 'root' })
export class HarnessUiContextService {
  readonly #router = inject(Router, { optional: true })
  readonly #outlets = inject(ChildrenOutletContexts, { optional: true })
  readonly pageRevision = signal(0)

  constructor() {
    this.#router?.events
      ?.pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.pageRevision.update((revision) => revision + 1))
  }

  /**
   * Whether the maker is holding an edit this application version's harness would not see.
   *
   * The harness reads the last-saved state and commits against it, so a step must not run on top
   * of work that exists only in the browser. Nothing is registered here to make that visible: the
   * answer is read from the `canDeactivate` contract Designer's editors already implement for
   * `PendingChangesGuard`, on the components the primary outlet chain has activated. An editor
   * written later is covered the day it is guarded against losing changes, with nothing to
   * remember on this side.
   *
   * It is a floor, not a guarantee. A component that implements nothing, or answers with an
   * `Observable` there is no way to read synchronously, is unknown rather than clean — `false`
   * means "nothing on the page says otherwise", and the maker's own acknowledgement on the run
   * card is what stands in for the rest.
   */
  hasUnsavedChanges(appVersionId: string): boolean {
    return unsavedChangesOnPage(this.#outlets, appVersionId) === true
  }

  snapshot(appVersionId: string): HarnessUiContext | undefined {
    const root = this.#router?.routerState.snapshot.root
    const context = root ? contextFromRoute(root, appVersionId) : undefined
    if (context?.view !== 'resource_editor') {
      return context
    }
    const unsaved = unsavedChangesOnPage(this.#outlets, appVersionId)

    return unsaved === undefined ? context : { ...context, has_unsaved_changes: unsaved }
  }
}

/**
 * The dirty state of the page the maker is on, or `undefined` where nothing there can answer —
 * which is never reported as clean.
 *
 * Only the primary chain is walked, the same one the route metadata is read from: a component in a
 * named outlet is beside the editor, not the editor.
 */
export function unsavedChangesOnPage(
  outlets: ChildrenOutletContexts | null,
  appVersionId: string
): boolean | undefined {
  let context = outlets?.getContext(PRIMARY_OUTLET) ?? null
  let unsaved: boolean | undefined
  while (context) {
    const answer = ownsVersion(context.route, appVersionId) ? unsavedIn(context.outlet) : undefined

    if (answer !== undefined) {
      unsaved = (unsaved ?? false) || answer
    }
    context = context.children.getContext(PRIMARY_OUTLET)
  }
  return unsaved
}

function ownsVersion(route: ActivatedRoute | null, appVersionId: string): boolean {
  return (
    !!appVersionId &&
    !!route?.snapshot.pathFromRoot.some((entry) => entry.params['appVersionId'] === appVersionId)
  )
}

function unsavedIn(outlet: RouterOutletContract | null): boolean | undefined {
  const editor = (
    outlet?.isActivated ? outlet.component : null
  ) as Partial<ComponentCanDeactivate> | null

  if (typeof editor?.canDeactivate !== 'function') {
    return undefined
  }
  try {
    const canLeave = editor.canDeactivate()

    // An `Observable` answer is a question asked over time; there is no synchronous reading of it.
    return typeof canLeave === 'boolean' ? !canLeave : undefined
  } catch {
    // An editor that cannot say where it stands has not said it is clean.
    return undefined
  }
}

export function contextFromRoute(
  root: ActivatedRouteSnapshot,
  appVersionId: string
): HarnessUiContext | undefined {
  let route: ActivatedRouteSnapshot | undefined = root
  let metadata: ResourcePageMarker | null | undefined
  const params: Record<string, string> = {}
  let enumPath: unknown
  while (route) {
    Object.assign(params, route.params)
    if (Object.prototype.hasOwnProperty.call(route.data, RESOURCE_PAGE_DATA_KEY)) {
      metadata = route.data[RESOURCE_PAGE_DATA_KEY] as ResourcePageMarker | null
    }
    enumPath = route.queryParams['path'] ?? enumPath
    route = route.children.find((child) => child.outlet === 'primary')
  }
  // The marker is shared with the issues panel and covers every manifest type; the harness
  // reports only the types it knows.
  if (
    !metadata ||
    !isHarnessResourceType(metadata.type) ||
    !isUuid(appVersionId) ||
    params['appVersionId'] !== appVersionId
  ) {
    return undefined
  }
  const resourceId = resourcePageId(metadata, params, { path: enumPath })
  if ((metadata.idParam || metadata.enumerationPath) && (!resourceId || !isUuid(resourceId))) {
    return undefined
  }
  return {
    schema_version: 1,
    app_version_id: appVersionId,
    view: resourceId ? 'resource_editor' : 'resource_list',
    resource_type: metadata.type,
    ...(resourceId && { resource_id: resourceId }),
    // Unknown editor dirty state is deliberately omitted, never claimed clean.
  }
}
